// ---------------------------------------------------------------------------
// Brainstorm Slice — drawer/modal state + candidate management + pick/dismiss events.
//
// State ownership:
//   Scalars (drawerOpen, modalExpandedId, activeCandidateId, loading, errorMessage,
//            dwellStartMs) — passed through BlockHost overrides.
//   Arrays/Objects (candidates, query, picked, events, onApply) — read by islands
//   via direct store subscription.
// ---------------------------------------------------------------------------

import type { Candidate, BrainstormQuery } from '@/features/brainstorm/repository'

export interface BrainstormEvent {
  kind: 'pick' | 'dismiss'
  candidate: Candidate | null
  dwellMs: number
  timestamp: string
}

export interface BrainstormSlice {
  brainstorm: {
    drawerOpen: boolean
    modalExpandedId: string
    activeCandidateId: string
    loading: boolean
    errorMessage: string
    dwellStartMs: number
    query: BrainstormQuery | Record<string, never>
    candidates: Candidate[]
    picked: Candidate[]
    events: BrainstormEvent[]
    onApply: ((c: Candidate) => void) | null
    setCandidates(next: Candidate[]): void
    setLoading(v: boolean): void
    setError(msg: string): void
    setActive(id: string): void
    expand(id: string): void
    collapse(): void
    apply(): void
    dismissAll(): void
    selectNext(): void
    selectPrev(): void
    resetBrainstorm(): void
  }
  openBrainstorm(args: { query: BrainstormQuery; onApply: (c: Candidate) => void }): Promise<void>
}

type Set_ = (fn: (state: BrainstormSlice) => void) => void
type Get_ = () => BrainstormSlice

export function createBrainstormSlice(
  set: Set_,
  get: Get_,
  _api: unknown,
): BrainstormSlice {
  return {
    brainstorm: {
      drawerOpen: false,
      modalExpandedId: '',
      activeCandidateId: '',
      loading: false,
      errorMessage: '',
      dwellStartMs: 0,
      query: {} as BrainstormQuery | Record<string, never>,
      candidates: [],
      picked: [],
      events: [],
      onApply: null,

      setCandidates: (next) => set((s) => { s.brainstorm.candidates = next }),
      setLoading: (v) => set((s) => { s.brainstorm.loading = v }),
      setError: (msg) => set((s) => { s.brainstorm.errorMessage = msg }),
      setActive: (id) => set((s) => { s.brainstorm.activeCandidateId = id }),

      expand: (id) => set((s) => {
        s.brainstorm.modalExpandedId = id
        s.brainstorm.activeCandidateId = id
      }),

      collapse: () => set((s) => {
        s.brainstorm.modalExpandedId = ''
      }),

      apply: () => {
        // Read candidate outside set() so onApply runs without immer draft constraints
        const brainstorm = get().brainstorm
        const candidate = brainstorm.candidates.find((c) => c.id === brainstorm.modalExpandedId) ?? null
        if (candidate && brainstorm.onApply) brainstorm.onApply(candidate)
        set((s) => {
          if (candidate) {
            s.brainstorm.picked.push(candidate)
            s.brainstorm.events.push({
              kind: 'pick',
              candidate,
              dwellMs: Date.now() - s.brainstorm.dwellStartMs,
              timestamp: new Date().toISOString(),
            })
          }
          s.brainstorm.drawerOpen = false
          s.brainstorm.modalExpandedId = ''
        })
      },

      dismissAll: () => set((s) => {
        s.brainstorm.events.push({
          kind: 'dismiss',
          candidate: null,
          dwellMs: Date.now() - s.brainstorm.dwellStartMs,
          timestamp: new Date().toISOString(),
        })
        s.brainstorm.drawerOpen = false
        s.brainstorm.modalExpandedId = ''
      }),

      selectNext: () => set((s) => {
        const { candidates, modalExpandedId } = s.brainstorm
        if (candidates.length === 0) return
        const idx = candidates.findIndex((c) => c.id === modalExpandedId)
        if (idx < 0) return
        const next = candidates[(idx + 1) % candidates.length]!
        s.brainstorm.modalExpandedId = next.id
        s.brainstorm.activeCandidateId = next.id
      }),

      selectPrev: () => set((s) => {
        const { candidates, modalExpandedId } = s.brainstorm
        if (candidates.length === 0) return
        const idx = candidates.findIndex((c) => c.id === modalExpandedId)
        if (idx < 0) return
        const prev = candidates[(idx - 1 + candidates.length) % candidates.length]!
        s.brainstorm.modalExpandedId = prev.id
        s.brainstorm.activeCandidateId = prev.id
      }),

      resetBrainstorm: () => set((s) => {
        s.brainstorm.drawerOpen = false
        s.brainstorm.modalExpandedId = ''
        s.brainstorm.activeCandidateId = ''
        s.brainstorm.loading = false
        s.brainstorm.errorMessage = ''
        s.brainstorm.dwellStartMs = 0
        s.brainstorm.query = {}
        s.brainstorm.candidates = []
        s.brainstorm.picked = []
        s.brainstorm.events = []
        s.brainstorm.onApply = null
      }),
    },

    openBrainstorm: async ({ query, onApply }) => {
      set((s) => {
        s.brainstorm.drawerOpen = true
        s.brainstorm.dwellStartMs = Date.now()
        s.brainstorm.query = query
        s.brainstorm.onApply = onApply
        s.brainstorm.candidates = []
        s.brainstorm.loading = true
        s.brainstorm.errorMessage = ''
        s.brainstorm.modalExpandedId = ''
      })
      try {
        const { getBrainstormProvider } = await import('@/features/brainstorm/providers')
        const provider = getBrainstormProvider()
        const candidates = await provider.findCandidates(query)
        set((s) => {
          s.brainstorm.candidates = candidates
          s.brainstorm.loading = false
        })
      } catch (err) {
        set((s) => {
          s.brainstorm.errorMessage = err instanceof Error ? err.message : String(err)
          s.brainstorm.loading = false
        })
      }
    },
  }
}
